import React, { useState, useEffect } from 'react';
import { getScheduleWeek, getOpponents } from '../api';
import { FRONT_ZONES, BACK_ZONES, BODY_OUTLINE_PATHS } from '../components/BodyPartSelector';

// ── Same shape as WeekSchedule.js's own small date helpers — kept local rather than shared
// since every other /print/* page in this app is self-contained (MatchChronicPrint,
// PhysicalTestingPrint), not importing from the screen version of the page. ──
function toDateStr(d) {
  if (typeof d === 'string') return d.slice(0, 10);
  return d.toISOString().slice(0, 10);
}
function fmtDayFull(dateStr) {
  const [y, m, d] = toDateStr(dateStr).split('-');
  return `${d}-${m}-${y.slice(2)}`;
}
function fmtWeekRange(days) {
  if (!days?.length) return '';
  const [, m1, d1] = toDateStr(days[0].schedule_date).split('-');
  const [, m2, d2] = toDateStr(days[days.length - 1].schedule_date).split('-');
  return `${d1}/${m1} – ${d2}/${m2}`;
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const SESSION_LABELS = { none: '—', pitch: 'Pitch', gym: 'Gym', individual: 'Individual' };

const fmt0 = n => (n == null || n === '' || isNaN(n) ? '—' : Math.round(Number(n)).toLocaleString());
const fmtRange = (lo, hi) => (lo == null && hi == null) ? '—' : `${fmt0(lo)}–${fmt0(hi)}`;

// ── Print-safe body silhouette — same zone geometry as the live BodyPartSelector, but
// drawn with fixed colors instead of var(--...) theme tokens. Printing under a dark-mode
// session would otherwise draw a dark-background diagram on a white printed page. ──
function PrintSilhouette({ zones, active, width = 40 }) {
  const height = width * 2.1; // viewBox is 200x420, same aspect ratio
  const p = BODY_OUTLINE_PATHS;
  return (
    <svg viewBox="0 0 200 420" width={width} height={height} style={{ display: 'block' }}>
      <g fill="#ffffff" stroke="#9ca3af" strokeWidth={1.5}>
        <circle cx={p.circle.cx} cy={p.circle.cy} r={p.circle.r} />
        <rect x={p.neck.x} y={p.neck.y} width={p.neck.width} height={p.neck.height} rx={p.neck.rx} />
        <path d={p.torso} />
        <rect x={p.armLeft.x} y={p.armLeft.y} width={p.armLeft.width} height={p.armLeft.height} rx={p.armLeft.rx} />
        <rect x={p.armRight.x} y={p.armRight.y} width={p.armRight.width} height={p.armRight.height} rx={p.armRight.rx} />
        <rect x={p.legLeft.x} y={p.legLeft.y} width={p.legLeft.width} height={p.legLeft.height} rx={p.legLeft.rx} />
        <rect x={p.legRight.x} y={p.legRight.y} width={p.legRight.width} height={p.legRight.height} rx={p.legRight.rx} />
        <ellipse cx={p.footLeft.cx} cy={p.footLeft.cy} rx={p.footLeft.rx} ry={p.footLeft.ry} />
        <ellipse cx={p.footRight.cx} cy={p.footRight.cy} rx={p.footRight.rx} ry={p.footRight.ry} />
      </g>
      {zones.map(zone => {
        const isActive = active.includes(zone.id);
        return zone.rects.map((r, i) => (
          <rect key={`${zone.id}-${i}`} x={r.x} y={r.y} width={r.w} height={r.h} rx={6}
            fill={isActive ? 'rgba(239,68,68,0.55)' : 'rgba(148,163,184,0.12)'}
            stroke={isActive ? '#ef4444' : '#d1d5db'}
            strokeWidth={isActive ? 1.5 : 1}
          />
        ));
      })}
    </svg>
  );
}

function BodyDiagram({ bodyParts }) {
  const front = bodyParts?.front || [];
  const back = bodyParts?.back || [];
  return (
    <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <PrintSilhouette zones={FRONT_ZONES} active={front} />
        <div style={{ fontSize: 5.5, color: '#9ca3af', marginTop: 1 }}>Front</div>
      </div>
      <div style={{ textAlign: 'center' }}>
        <PrintSilhouette zones={BACK_ZONES} active={back} />
        <div style={{ fontSize: 5.5, color: '#9ca3af', marginTop: 1 }}>Back</div>
      </div>
    </div>
  );
}

// ── Small colored pill matching the live page's segmented-button / badge colors ──
function Pill({ children, active, color = '#3b82f6' }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: 6.5, fontWeight: 700, padding: '1px 5px', borderRadius: 99,
      background: active ? color : '#f3f4f6',
      color: active ? '#fff' : '#9ca3af',
    }}>
      {children}
    </span>
  );
}

// ── One printed day column ────────────────────────────────────────────────────
function DayColumn({ day, dayIndex, logoUrl }) {
  const isMatch = day.is_match_day;
  // Same "off day" rule as the live page: both Dagindeling toggles left on none/none.
  const isOffDay = !isMatch && (day.session_am_type || 'none') === 'none' && (day.session_pm_type || 'none') === 'none';
  return (
    <div style={{
      border: isMatch ? '1px solid #fca5a5' : '1px solid #d1d5db',
      borderRadius: 4, padding: '5px 6px',
      background: isMatch ? '#fef2f2' : isOffDay ? '#f3f4f6' : '#fff',
      display: 'flex', flexDirection: 'column', gap: 4, minHeight: 0,
    }}>
      <div>
        <div style={{ fontSize: 8.5, fontWeight: 700, color: '#111827' }}>
          {DAY_NAMES[dayIndex]} {fmtDayFull(day.schedule_date)}
        </div>
        <div style={{ fontSize: 7, fontWeight: 600, color: '#6b7280', marginTop: 1 }}>
          {day.md_type || 'Unscheduled'}
        </div>
      </div>

      {isMatch && (
        <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: 3, fontSize: 7 }}>
          <div style={{ marginBottom: 2 }}>
            <Pill active={day.home_away === 'home'} color="#10b981">Home</Pill>{' '}
            <Pill active={day.home_away === 'away'} color="#3b82f6">Away</Pill>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {logoUrl && <img src={logoUrl} alt="" style={{ width: 12, height: 12, objectFit: 'contain' }} />}
            <span style={{ fontWeight: 700, color: '#111827' }}>{day.opponent_name || 'TBC'}</span>
          </div>
          <div style={{ color: '#6b7280', marginTop: 1 }}>
            {day.kickoff_time ? day.kickoff_time.slice(0, 5) : 'Kickoff TBC'}
          </div>
        </div>
      )}

      {!isMatch && (
        <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: 3, fontSize: 7, color: '#374151' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
            <strong>AM</strong>
            <Pill active={(day.session_am_type || 'none') !== 'none'}>{SESSION_LABELS[day.session_am_type || 'none']}</Pill>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 3 }}>
            <strong>PM</strong>
            <Pill active={(day.session_pm_type || 'none') !== 'none'}>{SESSION_LABELS[day.session_pm_type || 'none']}</Pill>
          </div>
          <BodyDiagram bodyParts={day.body_parts} />
          <div style={{ marginTop: 3 }}><strong>PREP</strong> {[
            day.prep_warmup_field ? 'Warm-up' : null,
            day.prep_passing_drill ? 'Passing' : null,
          ].filter(Boolean).join(', ') || '—'}</div>
        </div>
      )}

      <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: 3, fontSize: 7, color: '#374151' }}>
        {!isMatch && (
          <>
            {day.tussenvorm && <div><strong>Tussenvorm</strong> {day.tussenvorm}</div>}
            {day.ssg_format && <div><strong>SSG</strong> {day.ssg_format}</div>}
            {day.area_per_player && <div><strong>Area/pl.</strong> {day.area_per_player}</div>}
          </>
        )}
        {day.notes && <div style={{ marginTop: 2 }}><strong>Notes</strong> {day.notes}</div>}
      </div>

      {!isMatch && (day.min_low != null || day.min_high != null || day.rpe_low != null || day.rpe_high != null || day.wissels_extra) && (
        <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: 3, fontSize: 7, color: '#374151' }}>
          {(day.min_low != null || day.min_high != null) && <div><strong>Min</strong> {fmtRange(day.min_low, day.min_high)}</div>}
          {(day.rpe_low != null || day.rpe_high != null) && <div><strong>RPE</strong> {fmtRange(day.rpe_low, day.rpe_high)}</div>}
          {day.wissels_extra && <div>Wissels extra</div>}
        </div>
      )}

      <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: 3, fontSize: 6.5, color: '#6b7280', marginTop: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>TD</span><span>{fmtRange(day.td_low, day.td_high)}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>HID</span><span>{fmtRange(day.hid_low, day.hid_high)}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>SD</span><span>{fmtRange(day.sd_low, day.sd_high)}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Short act.</span><span>{fmtRange(day.short_actions_low, day.short_actions_high)}</span></div>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>HMLD</span><span>{fmtRange(day.hmld_low, day.hmld_high)}</span></div>
      </div>
    </div>
  );
}

const PRINT_CSS = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  @media print {
    @page { size: A4 landscape; margin: 10mm; }
    body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none !important; }
  }
  @media screen {
    body { background: #d1d5db; font-family: Inter, Arial, sans-serif; }
    .loading-screen { display: flex; align-items: center; justify-content: center; height: 100vh; font-size: 15px; color: #6b7280; }
    .page { width: 277mm; height: 190mm; margin: 24px auto; background: white; box-shadow: 0 4px 24px rgba(0,0,0,0.18); padding: 10mm; box-sizing: border-box; }
  }
`;

export default function WeekSchedulePrint() {
  const [week, setWeek] = useState(null);
  const [opponents, setOpponents] = useState([]);
  const [status, setStatus] = useState('loading');
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    const weekStart = localStorage.getItem('print_week_schedule');
    if (!weekStart) { setErrMsg('No week selected. Close this tab and try again.'); setStatus('error'); return; }

    Promise.all([getScheduleWeek(weekStart), getOpponents()])
      .then(([w, opps]) => {
        if (!w) { setErrMsg('That week no longer exists.'); setStatus('error'); return; }
        setWeek(w);
        setOpponents(opps || []);
        setStatus('ready');
      })
      .catch(e => { setErrMsg(e.message); setStatus('error'); });
  }, []);

  useEffect(() => {
    if (status !== 'ready') return;
    const t = setTimeout(() => window.print(), 500);
    return () => clearTimeout(t);
  }, [status]);

  if (status === 'loading') {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />
        <div className="loading-screen no-print">Preparing week schedule…</div>
      </>
    );
  }
  if (status === 'error') {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />
        <div className="loading-screen no-print" style={{ color: '#ef4444' }}>{errMsg}</div>
      </>
    );
  }

  const findLogo = name => name ? opponents.find(o => o.name.toLowerCase() === name.toLowerCase())?.logo_url : null;

  const weeklyTotals = (() => {
    const mid = (lo, hi) => ((Number(lo) || 0) + (Number(hi) || 0)) / 2;
    const sumSdRange = values => {
      const sum = values.reduce((a, v) => a + v, 0);
      const mean = sum / values.length;
      const variance = values.reduce((a, v) => a + (v - mean) ** 2, 0) / values.length;
      const sd = Math.sqrt(variance);
      return [Math.max(0, sum - sd), sum + sd];
    };
    return {
      td: sumSdRange(week.days.map(d => mid(d.td_low, d.td_high))),
      hid: sumSdRange(week.days.map(d => mid(d.hid_low, d.hid_high))),
      sd: sumSdRange(week.days.map(d => mid(d.sd_low, d.sd_high))),
      short_actions: sumSdRange(week.days.map(d => mid(d.short_actions_low, d.short_actions_high))),
      hmld: sumSdRange(week.days.map(d => mid(d.hmld_low, d.hmld_high))),
    };
  })();

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />
      <section className="page" style={{ fontFamily: 'Inter, Arial, sans-serif', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2.5px solid #2563eb', paddingBottom: 6, marginBottom: 8 }}>
          <div>
            <div style={{ fontSize: 9, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.12em' }}>Week Schedule</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827', lineHeight: 1.1, marginTop: 2 }}>
              Week of {fmtWeekRange(week.days)} <span style={{ fontSize: 10, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase' }}>· {week.status}</span>
            </div>
          </div>
          <img src={`${process.env.PUBLIC_URL}/heracles-crest.png`} alt="Heracles Almelo" style={{ height: 32, width: 'auto', objectFit: 'contain' }} />
        </div>

        {/* Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr) 34mm', gap: 4, flex: 1, minHeight: 0 }}>
          {week.days.map((day, i) => (
            <DayColumn key={day.id} day={day} dayIndex={i} logoUrl={findLogo(day.opponent_name)} />
          ))}

          <div style={{ border: '1px solid #d1d5db', borderRadius: 4, padding: '5px 6px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 8, fontWeight: 700, color: '#111827', marginBottom: 3 }}>Weekly total</div>
            {[
              ['TD', weeklyTotals.td], ['HID', weeklyTotals.hid], ['SD', weeklyTotals.sd],
              ['Short act.', weeklyTotals.short_actions], ['HMLD', weeklyTotals.hmld],
            ].map(([lbl, [lo, hi]]) => (
              <div key={lbl} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 7, color: '#374151', marginBottom: 3 }}>
                <span>{lbl}</span>
                <span style={{ fontWeight: 700 }}>{fmtRange(lo, hi)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
